import { cn, NoddiIcon } from 'noddi-ui-common';
import { Checkbox } from 'radix-ui';
import { useId } from 'react';
import { NoddiTooltip } from '../Overlay/NoddiTooltip';

export type NoddiCheckboxProps = Omit<Checkbox.CheckboxProps, 'onCheckedChange'> & {
  label?: React.ReactNode;
  onCheckedChange?: (checked: boolean) => void;
  defaultChecked?: boolean;
  tooltip?: string;
  id?: string;
};

export const NoddiCheckbox = ({
  checked,
  onCheckedChange,
  label,
  defaultChecked,
  tooltip,
  id: providedId,
  ...props
}: NoddiCheckboxProps) => {
  const generatedId = useId();
  const id = providedId ?? generatedId;
  const handleCheckedChange = (value: Checkbox.CheckedState) => {
    if (onCheckedChange) {
      onCheckedChange(value === 'indeterminate' ? false : value);
    }
  };

  return (
    <div className='flex items-center gap-2'>
      <Checkbox.Root
        className={cn(
          'peer size-5 shrink-0 rounded-[4px] border border-input shadow-xs outline-hidden transition-shadow focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground dark:bg-input/30 dark:aria-invalid:ring-destructive/40 dark:data-[state=checked]:bg-primary dark:data-[state=indeterminate]:bg-primary'
        )}
        checked={checked}
        defaultChecked={defaultChecked}
        onCheckedChange={handleCheckedChange}
        id={id}
        {...props}
      >
        <Checkbox.Indicator className='flex items-center justify-center text-white transition-none'>
          <NoddiIcon name={checked === 'indeterminate' ? 'Minus' : 'Check'} />
        </Checkbox.Indicator>
      </Checkbox.Root>

      {label && (
        <div className='flex items-center gap-2'>
          <label className='cursor-pointer text-sm leading-none text-black md:text-base' htmlFor={id}>
            {label}
          </label>
          {tooltip && (
            <NoddiTooltip
              content={tooltip}
              label={typeof label === 'string' ? `More information about ${label}` : 'More information'}
            />
          )}
        </div>
      )}
    </div>
  );
};
